import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  getServerBookUrlFromUrl,
  useOpenBook,
  updateLinkTitles,
} from '../util/util';

// --- SVG Icons ---
// Using inline SVGs to avoid external dependencies.
const SparkleIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 3L9.5 8.5L4 11L9.5 13.5L12 19L14.5 13.5L20 11L14.5 8.5L12 3Z" />
    <path d="M5 3L6 5" />
    <path d="M18 19L19 21" />
    <path d="M3 18L5 19" />
    <path d="M19 5L21 6" />
  </svg>
);

const CopyIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const RegenerateIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

// --- Loading Skeleton Component ---
const LoadingSkeleton = () => {
  const [message, setMessage] = useState('Thinking.... 🤔');

  useEffect(() => {
    // Set a timer to change the message after 4 seconds
    const timer = setTimeout(() => {
      setMessage('Thinking deeply, thank you for your patience...');
    }, 4000); // 4000 milliseconds = 4 seconds

    // This is a cleanup function. It clears the timer if the component
    // is removed before the 4 seconds are up, preventing errors.
    return () => clearTimeout(timer);
  }, []); // The empty array [] means this effect runs only once when the component first renders

  return (
    <div className="animate-pulse space-y-3 p-1">
      {/* Added element to display the dynamic message */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{message}</p>

      <div className="h-4 bg-gray-200 rounded-md dark:bg-gray-700 w-1/3"></div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></div>
        <div className="h-3 bg-gray-200 rounded-md dark:bg-gray-700 w-5/6"></div>
        <div className="h-3 bg-gray-200 rounded-md dark:bg-gray-700 w-full"></div>
        <div className="h-3 bg-gray-200 rounded-md dark:bg-gray-700 w-3/4"></div>
      </div>
    </div>
  );
};

// --- AI Overview Component ---
const AIOverview = ({ query, onNewQuery }) => {
  const [isAIEnabled, setIsAIEnabled] = useState(() => {
    const savedPreference = localStorage.getItem('ai_overview_enabled');
    // Defaults to 'true' if no preference is saved
    return savedPreference !== 'false';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [overviewContent, setOverviewContent] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const openBookHandler = useOpenBook();
  const collapsibleContentRef = useRef(null); // Ref for the collapsible container

  // Effect for cleaning up expired cache on component mount
  useEffect(() => {
    const CACHE_PREFIX = 'ai_overview_query_';
    const TWO_HOURS_IN_MS = 2 * 60 * 60 * 1000;
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter((key) => key.startsWith(CACHE_PREFIX));

      cacheKeys.forEach((key) => {
        try {
          const item = JSON.parse(localStorage.getItem(key));
          if (item && item.timestamp) {
            const ageInMs = Date.now() - item.timestamp;
            if (ageInMs > TWO_HOURS_IN_MS) {
              localStorage.removeItem(key);
            }
          } else {
            // Remove item if it's malformed or lacks a timestamp
            localStorage.removeItem(key);
          }
        } catch (e) {
          // If parsing fails, the data is corrupt, so remove it
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.error('Failed to clean up local storage cache:', e);
    }
  }, []); // Empty array ensures this runs only once on mount

  useEffect(() => {
    localStorage.setItem('ai_overview_enabled', isAIEnabled);
  }, [isAIEnabled]);

  const fetchOverview = useCallback(
    async (forceRefetch = false) => {
      // Abort if the feature is disabled or there's no query
      if (!isAIEnabled || !query) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const cacheKey = `ai_overview_query_${query}`;
      const TWO_HOURS_IN_MS = 2 * 60 * 60 * 1000;

      // 1. Check for a cached result unless forcing a refetch
      if (!forceRefetch) {
        const cachedItemJSON = localStorage.getItem(cacheKey);
        if (cachedItemJSON) {
          const cachedItem = JSON.parse(cachedItemJSON);
          const ageInMs = Date.now() - cachedItem.timestamp;

          if (ageInMs < TWO_HOURS_IN_MS) {
            // Cache is valid, use it!
            setOverviewContent(cachedItem.data.content);
            setSuggestions(cachedItem.data.suggestions);
            setIsLoading(false);
            return; // Stop execution
          } else {
            // Cache is expired, remove it
            localStorage.removeItem(cacheKey);
          }
        }
      }
      setOverviewContent('');
      setSuggestions([]);
      setIsExpanded(false);

      const prompt = query;
      const apiMessages = [{ role: 'user', content: prompt }];
      const API_ENDPOINT =
        'https://r5qak3hi6g.execute-api.ap-south-1.amazonaws.com/chat';

      try {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'force-cache',
          body: JSON.stringify({ messages: apiMessages }),
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `API error: ${response.status} ${response.statusText} - ${errorBody}`
          );
        }

        const result = await response.json();
        const text = result.response;
        const updatedText = await updateLinkTitles(text);

        if (updatedText) {
          const suggestionRegex = /\[SUGGESTION\](.*?)\[\/SUGGESTION\]/g;
          const matches = [...updatedText.matchAll(suggestionRegex)];
          const extractedSuggestions = matches
            .map((match) => match[1].trim())
            .filter((suggestion) => suggestion !== query);

          setSuggestions(extractedSuggestions);

          const cleanedContent = updatedText
            .replace(suggestionRegex, '')
            .trim();
          setOverviewContent(cleanedContent);
          const itemToCache = {
            data: {
              content: cleanedContent,
              suggestions: extractedSuggestions,
            },
            timestamp: Date.now(),
          };
          localStorage.setItem(cacheKey, JSON.stringify(itemToCache));
        } else {
          throw new Error('No content was received from the AI model.');
        }
      } catch (e) {
        console.error('Failed to fetch AI overview:', e);
        setError(
          'Sorry, the AI overview could not be generated at this time. Please try again later.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [query, isAIEnabled]
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleRegenerate = () => {
    fetchOverview(true); // Pass true to force a refetch
  };
  const handleToggle = () => {
    setIsAIEnabled((prev) => !prev);
  };
  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCopy = () => {
    const textToCopy = `Query: ${query}\n\n${overviewContent}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const handleSuggestionClick = (suggestion) => {
    if (onNewQuery) {
      onNewQuery(suggestion);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 md:p-6 my-4 font-sans shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
          <div className="flex items-center gap-2">
            <SparkleIcon className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
              AI Overview
            </h2>
          </div>
          <a
            href="https://medium.com/@baws.in/introducing-pradnya-the-librarian-at-baws-in-a5810fae7c97"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-800 rounded-md dark:bg-blue-900/60 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
          >
            Powered by Pradnya
          </a>
        </div>
        <label htmlFor="ai-toggle" className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              id="ai-toggle"
              type="checkbox"
              className="sr-only"
              checked={isAIEnabled}
              onChange={handleToggle}
            />
            <div className="block bg-gray-300 dark:bg-gray-600 w-12 h-6 rounded-full"></div>
            <div
              className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isAIEnabled ? 'transform translate-x-6 bg-blue-500' : ''}`}
            ></div>
          </div>
        </label>
      </div>
      {isLoading}

      {error && !isLoading && (
        <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-3 rounded-lg">
          {error}
        </div>
      )}
      {isAIEnabled && (
        <>
          {isLoading && <LoadingSkeleton />}
          {error && !isLoading && (
            <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-3 rounded-lg">
              {error}
            </div>
          )}
          {!isLoading && !error && overviewContent && (
            <>
              <div
                ref={collapsibleContentRef}
                className="transition-all duration-500 ease-in-out overflow-hidden relative"
                style={{
                  maxHeight: isExpanded
                    ? `${collapsibleContentRef.current?.scrollHeight}px`
                    : '6rem',
                }}
              >
                {!isExpanded && (
                  <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-gray-50 dark:from-gray-800 to-transparent pointer-events-none" />
                )}
                <blockquote className="mb-4 border-l-4 border-gray-300 dark:border-gray-600 pl-4">
                  <p className="font-semibold text-gray-800 dark:text-gray-200">
                    {query}
                  </p>
                </blockquote>
                <div className="prose prose-blue dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          onClick={(e) => openBookHandler(e, props.href)}
                          className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {overviewContent}
                  </ReactMarkdown>
                </div>

                <div className="mt-3 flex items-center justify-end">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCopy}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Copy overview"
                    >
                      {isCopied ? (
                        <span className="text-sm text-blue-600 dark:text-blue-400">
                          Copied!
                        </span>
                      ) : (
                        <CopyIcon className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={handleRegenerate}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Regenerate overview"
                    >
                      <RegenerateIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {suggestions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                      Suggested Follow-ups:
                    </h3>
                    <div className="flex flex-wrap gap-2 justify-start">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-full hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-center">
                <button
                  onClick={toggleExpansion}
                  className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-2 px-5 rounded-full shadow-sm transition-colors text-sm"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              </div>
            </>
          )}
          <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
            AI can make mistakes! Your responses may be used to train our model!
          </p>
        </>
      )}
    </div>
  );
};

export default AIOverview;
